import { useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isStrongPassword from 'validator/es/lib/isStrongPassword';
import { useShallow } from 'zustand/react/shallow';

import DateOfBirthField from '@/components/Common/Date/DateOfBirh/DateOfBirthField.jsx';
import RegistrationConsent from '@/components/RegistrationConsent.jsx';

import { TextValidation } from '@/utils/constants.js';
import { isMobilePhoneNumber } from '@/utils/formUtils.js';
import { createQuestionObj } from '@/utils/helpers.js';

import { useBookingStore, usePersistedPrismicStore } from '@/store.js';

import DropdownQuestion from '../CustomAnamnesis/QuestionTypes/DropdownQuestion.jsx';
import TextfieldQuestion from '../CustomAnamnesis/QuestionTypes/TextfieldQuestion.jsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        position: 'relative',
        '& [data-testid^="question-"]': {
            border: 'none',
            padding: 0,
        },
    },
    questionGroupContainer: {
        display: 'flex',
        alignItems: 'start',
        gap: '1rem',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
    passwordRestriction: {
        fontSize: '0.875rem',
        fontWeight: theme.weight.heading,
    },
    passwordRestrictionsList: {
        margin: 0,
        padding: '0 1rem',
    },
    errorMessage: {
        color: '#da3030',
        fontSize: '0.75rem',
        marginTop: '0.5rem',
    },
    dobField: {
        width: '100%',
        boxSizing: 'border-box',
    },
    dobRequiredAsterisk: {
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.common.red,
            fontSize: '1.2em',
        },
    },
}));

const Registration = ({ isFromHash = false }) => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
    const {
        [i18n.language]: { benefits: prismicData },
    } = usePersistedPrismicStore((state) => state.registerData);
    const {
        selectedGender,
        firstName,
        lastName,
        dob,
        email,
        phoneNumber,
        confirmPassword,
        password,
        isEmailInUse,
        previouslyTriedEmail,
        isConsentConfirmed,
        isAnalyticsConfirmed,
    } = useBookingStore((state) => state.registrationFormData);
    const {
        loading,
        setSelectedGender,
        setDob,
        setFirstName,
        setLastName,
        setEmail,
        setPhoneNumber,
        setPassword,
        setConfirmPassword,
        setIsConsentConfirmed,
        setIsAnalyticsConfirmed,
    } = useBookingStore(
        useShallow((state) => ({
            loading: state.loading,
            setSelectedGender: state.setSelectedGender,
            setDob: state.setDob,
            setFirstName: state.setFirstName,
            setLastName: state.setLastName,
            setEmail: state.setEmail,
            setPhoneNumber: state.setPhoneNumber,
            setPassword: state.setPassword,
            setConfirmPassword: state.setConfirmPassword,
            setIsConsentConfirmed: state.setIsConsentConfirmed,
            setIsAnalyticsConfirmed: state.setIsAnalyticsConfirmed,
        }))
    );

    const dobSeparator = i18n.language === 'en' ? '/' : '.';
    const isoToDisplay = (iso) => (iso?.length === 10 ? iso.split('-').reverse().join(dobSeparator) : '');
    const [dobDisplay, setDobDisplay] = useState(() => isoToDisplay(dob));
    const [dobError, setDobError] = useState(false);

    const handleDobChange = (value) => {
        setDobDisplay(value);

        const [day, month, year] = value.split(dobSeparator);
        setDob(value.length === 10 ? `${year}-${month}-${day}` : '');
    };

    const handleDobError = (hasError) => {
        setDobError(hasError);
        if (hasError) setDob('');
    };

    const prevSeparatorRef = useRef(dobSeparator);
    useEffect(() => {
        if (prevSeparatorRef.current !== dobSeparator) {
            prevSeparatorRef.current = dobSeparator;
            setDobDisplay(isoToDisplay(dob));
        }
    });

    // Helper functions for dynamic error messages
    const getEmailErrorMessage = () => {
        if (isEmailInUse && email === previouslyTriedEmail) {
            return prismicData.used_email;
        }
        return prismicData.required_field_error_message;
    };

    const getConfirmPasswordErrorMessage = () => {
        if (confirmPassword && confirmPassword !== password) {
            return prismicData.same_password;
        }
        return prismicData.required_field_error_message;
    };

    const getPhoneErrorMessage = () => {
        if (!phoneNumber) {
            return prismicData.required_field_error_message;
        }
        if (!isMobilePhoneNumber(phoneNumber)) {
            return prismicData.phone_validation || t('PhoneFormat');
        }
        return prismicData.required_field_error_message;
    };

    return (
        <div className={classes.container}>
            {loading && (
                <div className={classes.loadingOverlay}>
                    <EternoSpinner />
                </div>
            )}
            <div className={classes.questionGroupContainer}>
                <DropdownQuestion
                    question={createQuestionObj('', prismicData.gender_label, 'gender', {
                        dropdown_options: prismicData.gender_values
                            .filter(
                                ({ gender_display_value: genderDisplayValue }) => genderDisplayValue[0].text !== '-'
                            )
                            .map(({ gender_display_value: genderDisplayValue }) => ({
                                option: [{ text: genderDisplayValue[0].text }],
                            })),
                    })}
                    answer={selectedGender}
                    setAnswer={(value) => setSelectedGender(value)}
                    hideTitle
                    noMarginTop
                    required
                    requiredErrorMessage={prismicData.required_field_error_message}
                />
                <div className={clsx(classes.dobField, classes.dobRequiredAsterisk)}>
                    <DateOfBirthField
                        dob={dobDisplay}
                        setDOB={handleDobChange}
                        error={dobError}
                        setError={handleDobError}
                        separator={dobSeparator}
                        required
                        mainHeading={prismicData.date_of_birth_label}
                        placeHolderDay={t('DatePlaceholderDay')}
                        placeHolderMonth={t('DatePlaceholderMonth')}
                        placeHolderYear={t('DatePlaceholderYear')}
                        requiredDateField={prismicData.required_field_error_message}
                        errorMessage={t('FormsDobInvalidFormat')}
                        showCalendarButton={!matchesXs}
                    />
                </div>
            </div>
            <div className={classes.questionGroupContainer}>
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.first_name_label, 'first-name')}
                    autoComplete="given-name"
                    answer={firstName}
                    setAnswer={(value) => setFirstName(value)}
                    customValidationErrorMessage={prismicData.required_field_error_message}
                    hideTitle
                    required
                />
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.last_name_label, 'last-name')}
                    autoComplete="family-name"
                    answer={lastName}
                    setAnswer={(value) => setLastName(value)}
                    customValidationErrorMessage={prismicData.required_field_error_message}
                    hideTitle
                    required
                />
            </div>
            <div className={classes.questionGroupContainer}>
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.email_label, 'email', {
                        textfield_validation: TextValidation.EMAIL,
                    })}
                    autoComplete="email"
                    answer={email}
                    setAnswer={(value) => setEmail(value)}
                    hideTitle
                    customValidation={(value) => !(isEmailInUse && value === previouslyTriedEmail)}
                    customValidationErrorMessage={getEmailErrorMessage()}
                    disabled={isFromHash}
                    required
                />
                {/* TODO replace with proper phone number input field */}

                <TextfieldQuestion
                    // eslint-disable-next-line no-underscore-dangle
                    question={createQuestionObj('', prismicData.mobile_number__optional_, 'phone-number')}
                    type="tel"
                    autoComplete="tel"
                    answer={phoneNumber}
                    setAnswer={(value) => setPhoneNumber(value)}
                    hideTitle
                    customValidation={(value) => isMobilePhoneNumber(value)}
                    customValidationErrorMessage={getPhoneErrorMessage()}
                    required
                />
            </div>
            <div className={classes.questionGroupContainer}>
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.password_label, 'password')}
                    answer={password}
                    setAnswer={(value) => setPassword(value)}
                    hideTitle
                    type="password"
                    autoComplete="new-password"
                    required
                    customValidationErrorMessage={prismicData.required_field_error_message}
                    customValidation={(value) =>
                        isStrongPassword(value, {
                            minLength: 8,
                            minNumbers: 1,
                            minSymbols: 1,
                            minUppercase: 0,
                            minLowercase: 0,
                        })
                    }
                />
                <TextfieldQuestion
                    question={createQuestionObj('', prismicData.confirm_password_label, 'confirm-password')}
                    answer={confirmPassword}
                    required
                    setAnswer={(value) => setConfirmPassword(value)}
                    hideTitle
                    type="password"
                    autoComplete="new-password"
                    customValidation={(value) => value === password}
                    customValidationErrorMessage={getConfirmPasswordErrorMessage()}
                />
            </div>
            {password.length > 0 && (
                <div>
                    <Typography className={classes.passwordRestriction}>
                        {prismicData.password_restrictions_heading}
                    </Typography>
                    <ul className={classes.passwordRestrictionsList}>
                        {prismicData.password_restrictions.map(({ password_restriction: passwordRestriction }) => (
                            <li key={passwordRestriction[0].text}>
                                <Typography className={classes.passwordRestriction}>
                                    {passwordRestriction[0].text}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <RegistrationConsent
                isConsentConfirmed={isConsentConfirmed}
                isAnalyticsConfirmed={isAnalyticsConfirmed}
                setIsConsentConfirmed={setIsConsentConfirmed}
                setIsAnalyticsConfirmed={setIsAnalyticsConfirmed}
            />
        </div>
    );
};

export default Registration;
